/* eslint-disable */
import styled from 'styled-components';

import React, { useLayoutEffect, useRef } from 'react';

import {
  emptyClipboard,
  isFirefoxBrowser,
  detectMobile,
} from '../../utils/utils';

export const PreventCopyPastInput = styled.input`
  width: 0.1px;
  height: 0.1px;
  overflow: hidden;
  display: none;
`;
/**
 * Prevents bringing outside text into the test.
 * Desktop: clears clipboard on focus/blur; on focus, blocks the first paste/click
 * after returning to the tab (preventPasteHandler).
 * Mobile: OS clipboard bar uses beforeinput (insertText), not paste. We block multi-
 * character insertText on mobile only (no keydown timing — that race fails at full
 * speed but passes with DevTools open). Listeners use useLayoutEffect so they exist
 * before paint (useEffect can miss fast clipboard taps).
 */
export default function PreventCopyPast() {
  const preventCopyRef = useRef(null);

  useLayoutEffect(() => {
    const clearClipboard = () => {
      emptyClipboard(preventCopyRef.current);
    };
    clearClipboard();

    const isMobile = detectMobile();

    const PASTE_INPUT_TYPES = new Set([
      'insertFromPaste',
      'insertFromPasteAsQuote',
    ]);
    const blockBeforeInputCapture = event => {
      if (event.isComposing) return;
      const { inputType } = event;
      if (inputType && PASTE_INPUT_TYPES.has(inputType)) {
        event.preventDefault();
        return;
      }
      if (
        isMobile &&
        inputType === 'insertText' &&
        event.data &&
        event.data.length >= 2
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener('beforeinput', blockBeforeInputCapture, true);

    const preventPasteHandler = event => {
      event.preventDefault();
      clearClipboard();
      window.removeEventListener('paste', preventPasteHandler);
      window.removeEventListener('click', preventPasteHandler);
    };

    const onWindowFocus = () => {
      window.addEventListener('paste', preventPasteHandler);
      window.addEventListener('click', preventPasteHandler);
      /**
       * In some OS the middle button acts as a shortcut for paste, with
       * out the clipboard. so disable the event
       */
      if (!window.onauxclick)
        window.onauxclick = event => event.preventDefault();
      clearClipboard();
    };

    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('blur', () => {
      if (isFirefoxBrowser()) {
        setTimeout(() => {
          if (!document.hasFocus()) clearClipboard();
        }, 0);
      } else if (!document.hasFocus()) {
        clearClipboard();
      }
    });

    return () => {
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('paste', preventPasteHandler);
      window.removeEventListener('click', preventPasteHandler);
      document.removeEventListener(
        'beforeinput',
        blockBeforeInputCapture,
        true
      );
    };
  }, []);

  return <PreventCopyPastInput ref={preventCopyRef} type="text" value="" />;
}
